import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  Button, 
  Box, 
  Paper, 
  Fade,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert
} from '@mui/material';
import BaseRound from './BaseRound';
import { useGame } from '../../contexts/GameContext';
import { predefinedRules } from '../../data/gameData';

export default function RuleRound() {
  const [customRule, setCustomRule] = useState('');
  const [selectedRule, setSelectedRule] = useState('');
  const [ruleType, setRuleType] = useState('global');
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [tempRule, setTempRule] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [drinkCount, setDrinkCount] = useState(1);
  const { state, setGlobalRule, addPersonalRule } = useGame();

  const handleCustomRuleSubmit = () => {
    if (customRule.trim()) {
      setTempRule(customRule);
      setSelectedRule(customRule);
      setDrinkCount(Math.floor(Math.random() * 2) + 1);
    }
  };

  const handlePredefinedRule = () => {
    const rules = ruleType === 'global' ? predefinedRules.global : predefinedRules.personal;
    const randomRule = rules[Math.floor(Math.random() * rules.length)];
    const ruleText = ruleType === 'personal' && selectedPlayer
      ? randomRule.replace('{player}', selectedPlayer)
      : randomRule;

    setTempRule(ruleText);
    setSelectedRule(ruleText);
    setDrinkCount(Math.floor(Math.random() * 3) + 1);
  };

  const handleConfirmRule = () => {
    if (tempRule) {
      if (ruleType === 'global') {
        setGlobalRule(`${tempRule} (${drinkCount} ${drinkCount === 1 ? 'Schluck' : 'Schlücke'})`);
      } else {
        addPersonalRule(`${tempRule} (${drinkCount} ${drinkCount === 1 ? 'Schluck' : 'Schlücke'})`);
      }
      setShowResult(true);
    }
  };

  const handleRuleTypeChange = (event, newType) => {
    if (newType !== null) {
      setRuleType(newType);
      setSelectedRule('');
      setCustomRule('');
      setSelectedPlayer('');
      setTempRule(null);
    }
  };

  const handleCustomRuleChange = (e) => {
    if (ruleType === 'personal' && selectedPlayer) {
      // Remove the player name prefix if it exists
      const playerPrefix = selectedPlayer + ' ';
      let newValue = e.target.value;
      if (!newValue.startsWith(playerPrefix)) {
        // If the input doesn't start with the player name, add it
        newValue = playerPrefix + newValue.replace(playerPrefix, '');
      }
      setCustomRule(newValue);
    } else {
      setCustomRule(e.target.value);
    }
  };

  const handlePlayerChange = (e) => {
    const newPlayer = e.target.value;
    setSelectedPlayer(newPlayer);
    setCustomRule(newPlayer ? `${newPlayer} ` : ''); // Initialize with player name
  };

  const isPersonalRulesLimitReached = state.activeRules.personal.length >= 2;

  const resultData = showResult ? {
    title: 'Neue Regel!',
    success: false,
    drinks: drinkCount,
    player: state.activePlayer,
    points: 0,
    customText: `${tempRule} (Bestrafung: ${drinkCount} ${drinkCount === 1 ? 'Schluck' : 'Schlücke'}! 🍺)`
  } : null;

  return (
    <BaseRound
      title="Regelrunde"
      instructions={`${state.activePlayer}, erstelle eine neue Regel oder lass dir eine zufällige geben!`}
      showResult={showResult}
      resultData={resultData}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <ToggleButtonGroup
          value={ruleType}
          exclusive
          onChange={handleRuleTypeChange}
          fullWidth
          sx={{ mb: 1 }}
        >
          <ToggleButton 
            value="global" 
            sx={{ py: 1 }}
          >
            Globale Regel
          </ToggleButton>
          <ToggleButton 
            value="personal" 
            sx={{ py: 1 }}
          >
            Spieler-Regel
          </ToggleButton>
        </ToggleButtonGroup>

        {ruleType === 'global' && state.activeRules.global !== null && (
          <Alert severity="info">
            Es existiert bereits eine globale Regel. Diese wird ersetzt.
          </Alert>
        )}
        {ruleType === 'personal' && isPersonalRulesLimitReached && (
          <Alert severity="info">
            Es existieren bereits 2 persönliche Regeln. Die älteste Regel wird ersetzt.
          </Alert>
        )}

        {!showResult && (
          <>
            {ruleType === 'personal' && (
              <FormControl fullWidth>
                <InputLabel>Wähle einen Spieler</InputLabel>
                <Select
                  value={selectedPlayer}
                  label="Wähle einen Spieler"
                  onChange={handlePlayerChange}
                >
                  {state.players.map((player, index) => (
                    <MenuItem key={index} value={player.name}>
                      {player.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              label={ruleType === 'global' ? "Erstelle eine Regel für alle" : `Erstelle eine Regel für ${selectedPlayer}`}
              value={customRule}
              onChange={handleCustomRuleChange}
              variant="outlined"
              placeholder={ruleType === 'global' 
                ? "z.B. 'Trinke mit geschlossenen Augen'" 
                : selectedPlayer ? `${selectedPlayer} muss beim Trinken hüpfen` : "Wähle zuerst einen Spieler"}
              multiline
              rows={2}
              disabled={ruleType === 'personal' && !selectedPlayer}
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleCustomRuleSubmit}
                disabled={!customRule.trim() || (ruleType === 'personal' && !selectedPlayer)}
                fullWidth
              >
                Regel vorschlagen
              </Button>

              <Button
                variant="outlined"
                color="primary"
                onClick={handlePredefinedRule}
                disabled={ruleType === 'personal' && !selectedPlayer}
                fullWidth
              >
                Zufällige Regel
              </Button>
            </Box>

            {tempRule && (
              <Fade in timeout={500}>
                <Paper 
                  sx={{ 
                    mt: 2, 
                    p: 3, 
                    bgcolor: 'background.default',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'primary.main',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <strong>Vorgeschlagene {ruleType === 'global' ? 'globale' : 'persönliche'} Regel:</strong>
                    {tempRule}
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleConfirmRule}
                      sx={{ mt: 2 }}
                    >
                      Regel bestätigen
                    </Button>
                  </Box>
                </Paper>
              </Fade>
            )}
          </>
        )}
      </Box>
    </BaseRound>
  );
} 